import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Archive, Download, Eye, EyeOff, Loader2, Save, Trash2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { formatDateID, formatNumberInput, parseDigits } from "../lib/format";
import { ConfirmDialog } from "../components/Common";
import { Switch } from "../components/ui/switch";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function Pengaturan() {
  const { hasPerm } = useAuth();
  const [form, setForm] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [backups, setBackups] = useState([]);
  const [backingUp, setBackingUp] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoring, setRestoring] = useState(false);

  const loadBackups = useCallback(() => {
    api.get("/backups").then((r) => setBackups(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadBackups(); }, [loadBackups]);

  const backupNow = async () => {
    setBackingUp(true);
    try {
      await api.post("/backups/run");
      toast.success("Backup baru berhasil dibuat");
      loadBackups();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBackingUp(false);
    }
  };

  const doRestore = async () => {
    setRestoring(true);
    try {
      await api.post(`/backups/${restoreFile}/restore`, { confirm: true });
      toast.success("Restore berhasil — memuat ulang aplikasi");
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) {
      toast.error(errMsg(e));
      setRestoring(false);
    }
  };

  useEffect(() => {
    api.get("/settings")
      .then((r) => {
        setForm(r.data);
        setShowMenu(Boolean(r.data.show_special_menu));
      })
      .catch((e) => toast.error(errMsg(e)));
  }, []);

  const toggleMenu = async (v) => {
    setShowMenu(v);
    try {
      const { data: cur } = await api.get("/settings");
      await api.put("/settings", { ...cur, show_special_menu: v });
      window.dispatchEvent(new Event("settings:refresh"));
      toast.success(v ? "Menu khusus ditampilkan di sidebar" : "Menu khusus disembunyikan dari sidebar");
    } catch (e) {
      setShowMenu(!v);
      toast.error(errMsg(e));
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", form);
      toast.success("Pengaturan berhasil disimpan");
      window.dispatchEvent(new Event("saldo:refresh"));
      window.dispatchEvent(new Event("settings:refresh"));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const clearData = async () => {
    setClearing(true);
    try {
      const { data } = await api.post("/seed/clear");
      toast.success(data.message);
      setClearOpen(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setClearing(false);
    }
  };

  if (!form)
    return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-5 max-w-4xl" data-testid="pengaturan-page">
      <div className="bg-[#1E293B] border border-slate-800 rounded-xl shadow-sm p-6 space-y-5">
        <div>
          <h3 className="text-base font-bold text-slate-100">Identitas Laporan</h3>
          <p className="text-xs text-slate-400">Nama ini tampil pada header laporan export (Excel, CSV, PDF)</p>
        </div>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Nama Laporan</Label>
            <Input data-testid="input-report-name" value={form.report_name} onChange={(e) => setForm({ ...form, report_name: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>Nama Organisasi / Unit</Label>
            <Input data-testid="input-org-name" value={form.org_name} onChange={(e) => setForm({ ...form, org_name: e.target.value })} />
          </div>
        </div>
      </div>

      <div className="bg-[#1E293B] border border-slate-800 rounded-xl shadow-sm p-6 space-y-5">
        <div>
          <h3 className="text-base font-bold text-slate-100">Kas & Saldo</h3>
          <p className="text-xs text-slate-400">Saldo akhir = Saldo Awal + Total Pemasukan − Total Pengeluaran</p>
        </div>
        <div className="space-y-1.5">
          <Label>Saldo Awal Kas Resmi (Rp)</Label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400 font-semibold">Rp</span>
            <Input
              data-testid="input-opening-balance"
              className="pl-10 font-num"
              inputMode="numeric"
              value={formatNumberInput(form.opening_balance)}
              onChange={(e) => setForm({ ...form, opening_balance: parseDigits(e.target.value) })}
            />
          </div>
        </div>
        {hasPerm("view_saldo_imf") && (
          <div className="space-y-1.5">
            <Label>Saldo Awal Kas IMF (Rp)</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400 font-semibold">Rp</span>
              <Input
                data-testid="input-opening-balance-imf"
                className="pl-10 font-num"
                inputMode="numeric"
                value={formatNumberInput(form.opening_balance_imf)}
                onChange={(e) => setForm({ ...form, opening_balance_imf: parseDigits(e.target.value) })}
              />
            </div>
          </div>
        )}
        <div className="space-y-1.5">
          <Label>Batas Peringatan Saldo (Rp)</Label>
          <p className="text-[11px] text-slate-400">Banner peringatan muncul jika saldo kas berada di bawah nilai ini. Isi 0 untuk menonaktifkan.</p>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400 font-semibold">Rp</span>
            <Input
              data-testid="input-min-balance-alert"
              className="pl-10 font-num"
              inputMode="numeric"
              value={formatNumberInput(form.min_balance_alert)}
              onChange={(e) => setForm({ ...form, min_balance_alert: parseDigits(e.target.value) })}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Mata Uang</Label>
            <Input value={form.currency === "IDR" ? "IDR — Rupiah Indonesia (Rp)" : form.currency} disabled className="bg-[#0F172A]" />
          </div>
          <div className="space-y-1.5">
            <Label>Format Tanggal</Label>
            <Input value={form.date_format || "DD-MM-YYYY"} disabled className="bg-[#0F172A]" />
          </div>
        </div>
        <Button data-testid="btn-save-settings" onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Simpan Pengaturan
        </Button>
      </div>

      {hasPerm("view_special_expense") && (
        <div className="bg-[#1E293B] border border-slate-800 rounded-xl shadow-sm p-6 flex items-center justify-between gap-4">
          <div>
            <h3 className="text-base font-bold text-slate-100">Visibilitas Menu Khusus</h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Menampilkan menu tersembunyi di sidebar. Default: tersembunyi — halaman tetap bisa
              dibuka lewat URL langsung <span className="font-num">/khusus-admin</span>
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {showMenu ? <Eye className="w-4 h-4 text-slate-400" /> : <EyeOff className="w-4 h-4 text-slate-400" />}
            <Switch
              data-testid="switch-special-menu-settings"
              checked={showMenu}
              onCheckedChange={toggleMenu}
            />
          </div>
        </div>
      )}

      <div className="bg-[#1E293B] border border-slate-800 rounded-xl shadow-sm p-6 space-y-4">
        <div>
          <h3 className="text-base font-bold text-slate-100">Data Aplikasi</h3>
          <p className="text-xs text-slate-400">Kelola data transaksi secara keseluruhan</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            data-testid="btn-clear-data"
            onClick={() => setClearOpen(true)}
            className="border border-rose-500/30 bg-rose-500/15 text-rose-400 hover:bg-rose-100 font-medium px-4 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm"
          >
            <Trash2 className="w-4 h-4" /> Hapus Semua Transaksi
          </button>
        </div>
      </div>

      <div className="bg-[#1E293B] border border-slate-800 rounded-xl shadow-sm p-6 space-y-4" data-testid="backup-section">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-base font-bold text-slate-100">Backup & Restore</h3>
            <p className="text-xs text-slate-400">Backup otomatis harian pukul 02.00 WIB · disimpan 14 hari · maks 20 file terbaru</p>
          </div>
          <button
            data-testid="btn-backup-now"
            onClick={backupNow}
            disabled={backingUp}
            className="border border-slate-700 bg-[#1E293B] text-slate-200 hover:bg-slate-800/40 font-medium px-4 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm shadow-sm"
          >
            {backingUp ? <Loader2 className="w-4 h-4 animate-spin" /> : <Archive className="w-4 h-4" />}
            Backup Sekarang
          </button>
        </div>
        {backups.length === 0 ? (
          <p className="text-xs text-slate-400">Belum ada file backup.</p>
        ) : (
          <div className="divide-y divide-slate-800 border border-slate-800/60 rounded-lg overflow-hidden">
            {backups.map((b) => (
              <div key={b.file} className="flex items-center gap-3 px-3 py-2.5 bg-[#1E293B]" data-testid={`backup-row-${b.file}`}>
                <Archive className="w-4 h-4 text-slate-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-num font-semibold text-slate-100 truncate">{b.file}</p>
                  <p className="text-[10px] text-slate-400">
                    {formatDateID(String(b.created_at).slice(0, 10))} {String(b.created_at).slice(11, 19)} · {(b.size_bytes / 1024).toFixed(1)} KB
                  </p>
                </div>
                <a
                  data-testid={`btn-download-${b.file}`}
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/backups/${b.file}/download`}
                  className="text-xs font-semibold text-cyan-400 hover:text-amber-300 border border-slate-700 rounded-md px-2.5 py-1.5 flex items-center gap-1"
                >
                  <Download className="w-3.5 h-3.5" /> Unduh
                </a>
                <button
                  data-testid={`btn-restore-${b.file}`}
                  onClick={() => setRestoreFile(b.file)}
                  className="text-xs font-semibold text-rose-400 border border-rose-500/30 rounded-md px-2.5 py-1.5 hover:bg-rose-500/15"
                >
                  Restore
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={Boolean(restoreFile)}
        onOpenChange={(o) => !o && setRestoreFile(null)}
        title="Restore Backup?"
        description={`Seluruh data saat ini akan digantikan oleh isi file ${restoreFile || ""}. Backup keamanan dibuat otomatis sebelum restore dijalankan.`}
        onConfirm={doRestore}
        loading={restoring}
      />

      <ConfirmDialog
        open={clearOpen}
        onOpenChange={setClearOpen}
        title="Hapus Semua Transaksi?"
        description="Seluruh data transaksi akan dihapus permanen dan saldo kembali ke saldo awal. Tindakan ini tidak dapat dibatalkan."
        onConfirm={clearData}
        loading={clearing}
      />
    </div>
  );
}
